import { useState, KeyboardEvent } from "react"
import { Input } from "@/modules/shared/ui/components/input"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Button } from "@/modules/shared/ui/components/button"
import { X, Plus } from "lucide-react"

interface MultiInputProps {
  id?: string
  values: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  className?: string
}

export function MultiInput({
  id,
  values,
  onChange,
  placeholder,
  className,
}: MultiInputProps) {
  const [inputValue, setInputValue] = useState("")

  const addValue = () => {
    if (inputValue.trim() && !values.includes(inputValue.trim())) {
      onChange([...values, inputValue.trim()])
      setInputValue("")
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      addValue()
    }
  }

  const removeValue = (indexToRemove: number) => {
    onChange(values.filter((_, index) => index !== indexToRemove))
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex flex-wrap gap-2">
        {values.map((value, index) => (
          <Badge
            key={index}
            variant="secondary"
            className="px-2 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100"
          >
            {value}
            <button
              type="button"
              onClick={() => removeValue(index)}
              className="ml-1 hover:text-blue-900"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          id={id}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
        />
        <Button
          type="button"
          onClick={addValue}
          size="icon"
          disabled={!inputValue.trim()}
          className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white transition-colors"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
